import { lazy, Suspense, type ReactNode } from 'react';

import clsx from 'clsx';

import Typography from '~/shared/components/Typography';
import SavingOpportunityIcon from '~/shared/assets/icons/saving-opportunity.svg';
import { usePermission } from '~/shared/hooks/usePermission';
import type { Volume } from '~/shared/types/api';
import { canBeDatafied } from '~/shared/utils/volumeHelpers';
import { getDocsSiteURL } from '~/shared/utils/docsSiteURL';

import type { ExplorationVolume } from '../../../types/exploration';
import { explorationVolumeToFleetVolume } from '../../../lib/transformVolume';
import {
  resolveSavingOpportunityVariant,
  savingOpportunityInstallAgentCopy,
  savingOpportunityReadMoreLabel,
  savingOpportunityReadyCopy,
  savingOpportunityScopeCopy,
  savingOpportunityUnsupportedCopy,
} from '../savingOpportunityVariant';

const AutoScaleRenderer = lazy(
  () =>
    import(
      '~/fleet-manager/components/FleetManagerPage/AllVolumesPage/AllVolumesTable/AutoScaleRenderer'
    )
);

const shellBaseClass = 'flex flex-col px-2.5 py-3 gap-2 rounded-[9px]';

// Beta: hidden everywhere for the initial release, re-enable once redesigned. Gated here
// (not at each call site) so every render location — sidebar top card, volume detail panel,
// and any future one — is covered by a single flag instead of needing to remember each spot.
const showSavingOpportunityBanner = false;

export interface SavingOpportunityCardProps {
  volume?: ExplorationVolume;
  fleetVolume?: Volume;
  className?: string;
}

type SavingOpportunityShellProps =
  | {
      tone?: 'default';
      reason?: never;
      className?: string;
      children: ReactNode;
    }
  | {
      tone: 'unsupported';
      reason: string;
      className?: string;
      children: ReactNode;
    };

function SavingOpportunityShell({
  className,
  children,
  tone = 'default',
  reason,
}: SavingOpportunityShellProps) {
  const isUnsupported = tone === 'unsupported';

  return (
    <div
      className={clsx(
        shellBaseClass,
        isUnsupported ? 'bg-[#FCFCFC]' : 'bg-saving-opportunity',
        className
      )}
    >
      <div className="flex items-center gap-0.5">
        {isUnsupported ? (
          <Typography
            variant="p3"
            color="text-gray-700"
            fontWeight={500}
            className="font-family-outfit"
          >
            {reason}
          </Typography>
        ) : (
          <>
            <SavingOpportunityIcon />
            <Typography
              variant="p3"
              color="text-primary-400"
              fontWeight={500}
              className="font-family-outfit"
            >
              Saving opportunity
            </Typography>
          </>
        )}
      </div>
      {children}
    </div>
  );
}

function SavingOpportunityReadMore() {
  const href = getDocsSiteURL();

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${savingOpportunityReadMoreLabel} in documentation (opens in new tab)`}
      className="w-fit"
    >
      <Typography
        variant="caption"
        color="text-primary-900"
        className="font-family-inter underline hover:text-primary-700"
      >
        {savingOpportunityReadMoreLabel}
      </Typography>
    </a>
  );
}

export default function SavingOpportunityCard({
  volume,
  fleetVolume,
  className,
}: SavingOpportunityCardProps) {
  const canWrite = usePermission('datafy.write.autoscaling');

  if (!showSavingOpportunityBanner) {
    return null;
  }

  if (!volume) {
    return (
      <SavingOpportunityShell className={className}>
        <Typography variant="caption" color="text-primary-900" className="font-family-inter">
          {savingOpportunityScopeCopy}
        </Typography>
        <SavingOpportunityReadMore />
      </SavingOpportunityShell>
    );
  }

  const canToggleOn = fleetVolume ? Boolean(canBeDatafied(fleetVolume)) : volume.isDatafiable;
  const variant = resolveSavingOpportunityVariant(volume, canToggleOn);
  const showAutoScaleRenderer = canToggleOn && variant.kind === 'eligible';
  const autoScaleVolume = fleetVolume ?? explorationVolumeToFleetVolume(volume);

  if (variant.kind === 'hidden') {
    return null;
  }

  if (variant.kind === 'unsupported') {
    return (
      <SavingOpportunityShell className={className} tone="unsupported" reason={variant.reason}>
        <Typography variant="caption" color="text-primary-900" className="font-family-inter">
          {savingOpportunityUnsupportedCopy}
        </Typography>
        <SavingOpportunityReadMore />
      </SavingOpportunityShell>
    );
  }

  const bodyCopy =
    variant.kind === 'eligible'
      ? savingOpportunityReadyCopy
      : savingOpportunityInstallAgentCopy;

  return (
    <SavingOpportunityShell className={className}>
      {showAutoScaleRenderer ? (
        <div className="flex items-center gap-1.5 w-fit">
          <Suspense fallback={null}>
            <AutoScaleRenderer
              volume={autoScaleVolume}
              setSelectedVolumes={() => {}}
              canWrite={canWrite}
            />
          </Suspense>
          <Typography variant="p3" color="text-primary-900" className="font-family-inter">
            Autoscaling
          </Typography>
        </div>
      ) : null}
      <Typography variant="caption" color="text-primary-900" className="font-family-inter">
        {bodyCopy}
      </Typography>
      <SavingOpportunityReadMore />
    </SavingOpportunityShell>
  );
}
